import { useContext, useEffect, useRef, useState } from "react";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { Box, CircularProgress, ClickAwayListener } from "@mui/material";
import { ParagraphText, UBInput } from "../../../Libraries";
import { GET_PRODUCTS } from "../../../apis/configuration";
import { handleApi } from "../../../apis";
import styles from "./styles.module.css";
import { LanguageContext } from "../../../context/LanguageContext";
import { PlusIconUnFill } from "../../../assets/icons/icon";
import { validString } from "../../../utils";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../config/firebase";

const ProductSuggestion = ({
  name = "",
  setName = "",
  handleSetProduct = () => {},
  label,
  toogleProductionSuggestion = () => {},
  ...props
}) => {
  const [SearchName, setSearchName] = useState("");
  const debounceSearchName = useDebouncedValue(SearchName, 500);
  const [isLoading, setIsLoading] = useState(false);
  const [productList, setproductList] = useState([]);
  const [I18n] = useContext(LanguageContext);
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);
  const containerRef = useRef(null);
  const inputRef = useRef();
  let cancelToken = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    setActiveIndex(-1);
    itemRefs.current = [];
  }, [productList]);

  useEffect(() => {
    const container = containerRef.current;
    const item = itemRefs.current[activeIndex];

    if (!container || !item) return;

    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;

    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.offsetHeight;

    if (itemTop < containerTop) {
      container.scrollTop = itemTop;
    } else if (itemBottom > containerBottom) {
      container.scrollTop = itemBottom - container.offsetHeight;
    }
  }, [activeIndex]);

  useEffect(() => {
    if (debounceSearchName) {
      cancelPreviousCall();
      getProductSuggestion(debounceSearchName);
    } else {
      setproductList([]);
    }
  }, [debounceSearchName]);

  const cancelPreviousCall = () => {
    if (cancelToken.current)
      cancelToken.current.cancel(
        "New request appears, canceling previous request.",
      );
    cancelToken.current = axios.CancelToken.source();
  };

  const getProductSuggestion = (product_name) => {
    setIsLoading(true);
    handleApi(GET_PRODUCTS, { product_name }, cancelToken.current, navigate)
      .then((res) => {
        setIsLoading(false);
        setproductList(res.products);
      })
      .catch((err) => {
        if (err.message === "New request appears, canceling previous request.")
          return;
        setIsLoading(false);
      });
  };

  const handleDataChange = (value) => {
    const valid = validString(value);
    if (!valid) return;
    setIsLoading(true);
    setSearchName(value);
    toogleProductionSuggestion(Boolean(value));
    setName(value);
  };

  const handleSetProductName = (item) => {
    triggerLogEvent(analytics, "sc_product_suggestion_selected", { product_name: item?.product_name });
    setSearchName("");
    toogleProductionSuggestion(false);
    setName(item?.product_name);
    handleSetProduct(item);
  };

  const isInputFocused = () => {
    return inputRef.current === document.activeElement;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Tab" && SearchName && !isInputFocused()) {
        e.preventDefault();
        setSearchName("");
        toogleProductionSuggestion(false);
      }
      if (e.key === "Enter" && SearchName && isInputFocused() && activeIndex === -1) {
        setSearchName("");
        toogleProductionSuggestion(false);
        setproductList([]);
        return;
      }
      if (!productList?.length || !isInputFocused()) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < productList.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : productList.length - 1,
        );
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        handleSetProductName(productList[activeIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    inputRef,
    SearchName,
    isInputFocused,
    productList,
    activeIndex,
    handleSetProductName,
    setSearchName,
  ]);

  return (
    <Box flex={1} position={"relative"} className="ProductSuggestionWrapper">
      <UBInput
        inputId="product_suggestion_search"
        variant="standard"
        size="small"
        value={name}
        onChange={(e, value) => {
          handleDataChange(value)
        }}
        sx={{ fontFamily: "Outfit", paddingLeft: "0.5rem", fontSize: "12px" }}
        InputProps={{
          sx: {
            "& input": {
              fontSize: "12px",
              color: "black",
            },
          },
          disableUnderline: true,
        }}
        inputRef={inputRef}
        label={label}
        onBlur={(e) => {
          if (SearchName && !((productList ?? []).length > 0) && !isLoading)
            return;
          setTimeout(() => {
            setSearchName("");
            toogleProductionSuggestion(false);
          }, 500);
        }}
        fullWidth
        {...props}
      />

      {SearchName && (
        <ClickAwayListener onClickAway={() => { setSearchName(""); toogleProductionSuggestion(false); }}>
          <Box
            className={styles.itemSuggestionContainer}
            style={{ top: label ? "79px" : "48px" }}
          >
            <Box
              className={`${styles.itemSuggestInner} generic-scrollbar`}
              ref={containerRef}
            >
              <div className={styles.singleItemSuggest}>
                <ParagraphText className="fontSize12 foundationInk400">
                  {I18n.catalog_items}
                </ParagraphText>
              </div>
              {isLoading ? (
                <Box
                  height="40px"
                  display="flex"
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <CircularProgress size={20} />
                </Box>
              ) : (productList ?? []).length > 0 ? (
                productList.map((item, index) => (
                  <div
                    role="option"
                    aria-selected={activeIndex === index}
                    ref={(el) => (itemRefs.current[index] = el)}
                    onClick={() => {
                      handleSetProductName(item);
                    }}
                    className={`${styles.singleItemSuggest} hover_color_foundation_Blue transition_background_color pointer
      ${activeIndex === index ? "highlighted" : ""}
    `}
                  >
                    <ParagraphText className="fontSize12 newFontColor">
                      {item?.product_name ?? ""}
                    </ParagraphText>
                    <ParagraphText className="fontSize12 newFontColor">
                      Rs. {item?.price ?? 0}{" "}
                    </ParagraphText>
                  </div>
                ))
              ) : (
                <ParagraphText
                  className={`fontSize12 newFontColor ${styles.notProductFound}`}
                >
                  {I18n.item_not_found}
                  <span
                    className="barclayBlueColor pointer marginTop5 createYourOwnItem"
                    onClick={() => setSearchName("")}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) setSearchName("");
                    }}
                  >
                    <span className="plusIcon">
                      <PlusIconUnFill />
                    </span>
                    {I18n.create_your_own_item}
                  </span>
                </ParagraphText>
              )}
            </Box>
            <div className={styles.borderLine} />
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default ProductSuggestion;
